import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginAPI, registerAPI, getProfileAPI } from '../services/auth';
import { API_BASE_URL } from '../constants/suggestions';


const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within AppProvider');
  return context;
};

const getHistoryAPI = async (token) => {
  const response = await fetch(`${API_BASE_URL}/api/history`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  if (!response.ok) throw new Error('Failed to fetch history');
  return await response.json();
};

const deleteConversationAPI = async (token, conversationId) => {
  const response = await fetch(`${API_BASE_URL}/api/history/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  if (!response.ok) throw new Error('Failed to delete conversation');
  return await response.json();
};

export const AppProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('authToken'));
  const [appLoading, setAppLoading] = useState(true);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [theme, setTheme] = useState('light');
  
  const [conversations, setConversations] = useState({
    '1': { title: 'New Chat', messages: [] },
  });
  const [activeConversationId, setActiveConversationId] = useState('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const openAddUserModal = () => setIsAddUserModalOpen(true);
  const closeAddUserModal = () => setIsAddUserModalOpen(false);

  const processAndSetHistory = (messagesArray) => {
    if (!messagesArray || messagesArray.length === 0) return;

    const convos = {};

    messagesArray.forEach((msg) => {
      const convoId = msg.conversation_id;

      if (!convos[convoId]) {
        convos[convoId] = { title: 'Saved Chat', messages: [], persisted: true };
      }

      if (msg.role === 'user' && convos[convoId].messages.length === 0) {
        convos[convoId].title = msg.content.substring(0, 30) + (msg.content.length > 30 ? '...' : '');
      }

      convos[convoId].messages.push({
        role: msg.role === 'assistant' ? 'bot' : msg.role,
        content: msg.content,
        sources: msg.sources_metadata || [],
      });
    });

    if (Object.keys(convos).length > 0) {
      setConversations(convos);
      const keys = Object.keys(convos);
      setActiveConversationId(keys[keys.length - 1]);
    }
  };

  const loadUserHistory = useCallback(async (authToken) => {
    try {
      const history = await getHistoryAPI(authToken);
      processAndSetHistory(history);
    } catch (err) {
      console.error("Could not load history:", err);
    }
  }, []);

  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        try {
          const profile = await getProfileAPI(token);
          setUser(profile);
          setIsAuthenticated(true);
          await loadUserHistory(token); 
        } catch (error) {
          localStorage.removeItem('authToken');
          setToken(null);
        }
      }
      setAppLoading(false);
    };
    validateToken();
  }, [token, loadUserHistory]);

  useEffect(() => {
    document.body.className = theme;
  }, [theme]);

  const toggleTheme = () => setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));

  const login = async (email, password) => {
    const response = await loginAPI(email, password);
    localStorage.setItem('authToken', response.token);
    setToken(response.token);
    setUser(response.user);
    setIsAuthenticated(true);
    await loadUserHistory(response.token);
  };

  const register = async (username, email, password, user_type) => {
    return await registerAPI(username, email, password, user_type, token);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setConversations({ '1': { title: 'New Chat', messages: [] } });
    setActiveConversationId('1');
  };

  const messages = conversations[activeConversationId]?.messages || [];

  const setMessages = updater => {
    if (!activeConversationId) return;
    setConversations(prev => {
      const activeConvo = prev[activeConversationId];
      if (!activeConvo) return prev;
      const newMessages = typeof updater === 'function' ? updater(activeConvo.messages) : updater;
      return {
        ...prev,
        [activeConversationId]: { ...activeConvo, messages: newMessages },
      };
    });
  };

  const handleNewChat = () => {
    const emptyChatEntry = Object.entries(conversations).find(([id, convo]) => convo.messages.length === 0 && !convo.persisted);
    if (emptyChatEntry) {
      setActiveConversationId(emptyChatEntry[0]);
      if (window.innerWidth < 768) setIsSidebarOpen(false);
      return;
    }

    const newId = Date.now().toString(); // Creates a new unique conversation_id
    setConversations(prev => ({
      ...prev,
      [newId]: { title: 'New Chat', messages: [], persisted: false },
    }));
    setActiveConversationId(newId);
    if (window.innerWidth < 768) setIsSidebarOpen(false);
  };

  const deleteConversation = async conversationId => {
    const conversation = conversations[conversationId];
    if (!conversationId || !conversation) return;

    setError(null);

    if (conversation.persisted && token) {
      try {
        await deleteConversationAPI(token, conversationId);
      } catch (err) {
        setError(err.message || 'Failed to delete conversation');
        return;
      }
    }

    const remainingIds = Object.keys(conversations).filter(id => id !== conversationId);

    if (remainingIds.length === 0) {
      const newId = Date.now().toString();
      setConversations({ [newId]: { title: 'New Chat', messages: [], persisted: false } });
      setActiveConversationId(newId);
    } else {
      setConversations(prev => {
        const updated = { ...prev };
        delete updated[conversationId];
        return updated;
      });
      if (activeConversationId === conversationId) {
        setActiveConversationId(remainingIds[remainingIds.length - 1]);
      }
    }
  };

  const updateConversationTitle = title => {
    if (messages.length === 0 && activeConversationId) {
      setConversations(prev => ({
        ...prev,
        [activeConversationId]: {
          ...prev[activeConversationId],
          title: title.substring(0, 50) || 'New Chat',
        },
      }));
    }
  };

  const value = {
    theme, toggleTheme, isAuthenticated, user, token, appLoading,
    login, logout, register,
    isAddUserModalOpen, openAddUserModal, closeAddUserModal,
    conversations, setConversations,
    activeConversationId, setActiveConversationId,
    messages, setMessages,
    loading, setLoading, error, setError,
    handleNewChat, deleteConversation, updateConversationTitle,
    isSidebarOpen, setIsSidebarOpen,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};